import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Product, Store, SourcingMode, Shipment } from '../types';
import {
  computeEconomics,
  formatMoney,
  getAmazonStatusMeta,
  getAmazonProductUrl,
  getSellerCentralSkuUrl,
  showToast
} from '../utils';

interface LaunchViewProps {
  active: boolean;
  products: Product[];
  stores: Store[];
  activeStore: Store | null;
  selectedProduct: Product | null;
  sourcingMode: SourcingMode;
  shipments: Shipment[];
  sellerId: string;
  marketplaceId: string;
  onSelectProduct: (productId: string) => void;
  onChangeSourcingMode: (mode: SourcingMode) => void;
  onToggleStoreSync: (productId: string, storeId: string) => void;
  onPushSingleProductAmazon: (
    product: Product,
    overrides: { retailPrice: number; brandName: string },
    triggerEl: HTMLElement | null
  ) => Promise<void>;
  onQueueShipment: (record: Shipment) => void;
  onTriggerAutoDropship: (product: Product, channelLabel: string, triggerEl: HTMLElement | null) => void;
  onSaveCustomUsUrl: (productId: string, newUrl: string) => Promise<void>;
}

export const LaunchView: React.FC<LaunchViewProps> = ({
  active,
  products,
  stores,
  activeStore,
  selectedProduct,
  sourcingMode,
  shipments,
  sellerId,
  marketplaceId,
  onSelectProduct,
  onChangeSourcingMode,
  onToggleStoreSync,
  onPushSingleProductAmazon,
  onQueueShipment,
  onTriggerAutoDropship,
  onSaveCustomUsUrl
}) => {
  const p = selectedProduct || products[0];

  const [fobUnit, setFobUnit] = useState<string>('6.5');
  const [ddpUnit, setDdpUnit] = useState<string>('1.5');
  const [retailPrice, setRetailPrice] = useState<string>('39.99');
  const [batchSize, setBatchSize] = useState<string>('150');
  const [rfqScript, setRfqScript] = useState<string>('');
  const [usUrlInput, setUsUrlInput] = useState<string>('');
  const [brandName, setBrandName] = useState<string>('AURELIUS STUDIO');
  const [brandError, setBrandError] = useState<boolean>(false);
  const [warehouse, setWarehouse] = useState<string>('Amazon FBA (ONT8 — California)');
  const [pushingAmazon, setPushingAmazon] = useState<boolean>(false);
  const brandInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!p) return;
    const d = p.researchDossier || {};
    const isUsDropship = sourcingMode === 'us_dropship';
    setUsUrlInput(d.usDropshipUrl || d.cjExactUrl || '');

    if (isUsDropship) {
      const usCost = d.usDropshipCost || +(p.pricing.alibabaFobUnit + p.pricing.ddpShippingUnit + 5.5).toFixed(2);
      setFobUnit(String(usCost));
      setDdpUnit('0');
      setRetailPrice(String(p.pricing.amazonRetailPrice));
      setBatchSize('1');
      setRfqScript(
        `US DOMESTIC ZERO-INVENTORY DROPSHIP ROUTING — ${d.usDropshipSupplier || 'US 3PL Partner'}\n\n` +
          `Product: ${p.title} (Supplier SKU: ${d.usDropshipSku || 'CJ-US-01'})\n` +
          `Direct US Dropship Product Link: ${d.usDropshipUrl || 'https://cjdropshipping.com'}\n` +
          `Fulfillment Mode: Automatic Pay-Per-Order Domestic Dropship ($0 Upfront Inventory · 0 MOQ)\n` +
          `Landed Cost: $${usCost.toFixed(2)}/unit (Includes 2–4 Day ${d.usShippingCarrier || 'USPS/UPS Ground'} from ${
            d.usWarehouseLocation || 'US Warehouse'
          })\n` +
          `Auto-Order Webhook: ACTIVE — Automatically places blind-shipped order when customer checks out.`
      );
    } else {
      setFobUnit(String(p.pricing.alibabaFobUnit));
      setDdpUnit(String(p.pricing.ddpShippingUnit));
      setRetailPrice(String(p.pricing.amazonRetailPrice));
      setBatchSize(String(p.alibabaSupplier?.moq || 150));
      setRfqScript(p.amazonListing?.rfqMessage || '');
    }
  }, [p, sourcingMode]);

  const econ = useMemo(() => {
    if (!p) return null;
    return computeEconomics(p, activeStore, {
      alibabaFobUnit: parseFloat(fobUnit) || 0,
      ddpShippingUnit: parseFloat(ddpUnit) || 0,
      amazonRetailPrice: parseFloat(retailPrice) || 0,
      batchSize: parseInt(batchSize, 10) || 100
    });
  }, [p, activeStore, fobUnit, ddpUnit, retailPrice, batchSize]);

  const csvBlobUrl = useMemo(() => {
    if (!p || !econ || typeof window === 'undefined') return '#export';
    const headers = ['SKU', 'ASIN', 'Title', 'RetailPrice', 'LandedCost', 'NetProfitPerUnit', 'Quantity'];
    const row = [
      `AMZ-${p.id.toUpperCase()}`,
      p.asin || 'B0CX9K4M2P',
      `"${(p.amazonListing?.title || p.title).replace(/"/g, '""')}"`,
      econ.amazonRetailPrice,
      econ.landedUnitCost,
      econ.netProfitPerUnit,
      econ.batchSize
    ];
    const csvContent = `${headers.join(',')}\n${row.join(',')}\n`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    return URL.createObjectURL(blob);
  }, [p, econ]);

  if (!p || !econ) {
    return <div id="view-launch" className="workspace-view" role="tabpanel" aria-labelledby="tab-launch" hidden={!active} />;
  }

  const d = p.researchDossier || {};
  const isUsDropship = sourcingMode === 'us_dropship';
  const monthlyProfit = Math.round(econ.netProfitPerUnit * (p.monthlySalesEst || 600));
  const meta = getAmazonStatusMeta(p);
  const listing = p.amazonListing || {};
  const asin = listing.asin || p.asin;
  const sku = listing.amazonSku || p.sku;
  const amzUrl = getAmazonProductUrl(p, sellerId);
  const scUrl = getSellerCentralSkuUrl(p);

  const handleQueuePo = (e: React.FormEvent) => {
    e.preventDefault();
    if (!brandName.trim()) {
      setBrandError(true);
      brandInputRef.current?.focus();
      return;
    }
    setBrandError(false);
    const record: Shipment = {
      poNumber: `PO-${Date.now().toString().slice(-6)}`,
      productId: p.id,
      productTitle: p.title,
      brandName: brandName.trim(),
      warehouse,
      batchSize: parseInt(batchSize, 10) || 150
    };
    onQueueShipment(record);
  };

  return (
    <div id="view-launch" className={`workspace-view ${active ? 'active' : ''}`} role="tabpanel" aria-labelledby="tab-launch" hidden={!active}>
      <section className="view-section">
        <div className="container">
          <div className="section-bar">
            <div>
              <span className="section-kicker">UNIT ECONOMICS &amp; SUPPLIER DISPATCH</span>
              <h2>Profit Calculator &amp; Launch Console</h2>
            </div>
            <div className="preview-controls">
              <label htmlFor="launch-product-select" className="field-inline-label">
                Selected Product:
              </label>
              <select
                id="launch-product-select"
                className="input-select tabular"
                value={p.id}
                onChange={(e) => onSelectProduct(e.target.value)}
              >
                {products.map((prod) => (
                  <option key={prod.id} value={prod.id}>
                    #{prod.rank} · {prod.title}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="concierge-two-col">
            <div className="concierge-main-col">
              <div className="panel-card">
                <div className="panel-header">
                  <span className="step-pill tabular">STEP 01</span>
                  <h3>Choose Fulfillment Mode &amp; Adjust Pricing</h3>
                </div>

                <div className="sourcing-mode-bar" role="group" aria-label="Sourcing and Fulfillment Mode">
                  <button
                    type="button"
                    className={`sourcing-mode-btn ${isUsDropship ? 'active' : ''}`}
                    id="mode-us-dropship-btn"
                    data-sourcing-mode="us_dropship"
                    onClick={() => {
                      onChangeSourcingMode('us_dropship');
                      showToast('Switched to US Domestic Dropship ($0 Upfront Inventory · No Alibaba Needed)');
                    }}
                  >
                    🇺🇸 US Domestic Dropship ($0 Upfront Inventory · No Alibaba)
                  </button>
                  <button
                    type="button"
                    className={`sourcing-mode-btn ${!isUsDropship ? 'active' : ''}`}
                    id="mode-alibaba-bulk-btn"
                    data-sourcing-mode="alibaba_bulk"
                    onClick={() => {
                      onChangeSourcingMode('alibaba_bulk');
                      showToast('Switched to Alibaba Private Label FBA (Bulk Factory Margin)');
                    }}
                  >
                    🏭 Alibaba Private Label FBA (Highest Unit Margin)
                  </button>
                </div>

                <div className="launch-us-dropship-panel" id="launch-us-dropship-panel">
                  <div className="launch-us-top-row">
                    <div>
                      <span className="us-auto-badge tabular">⚡ AUTO-ORDER ON SALE: ACTIVE</span>
                      <strong id="launch-us-supplier-name">
                        {d.usDropshipSupplier || `${d.usWarehouseLocation || 'Ontario, CA US Warehouse'} ($0 MOQ · 2–4 Day Shipping)`}
                      </strong>
                    </div>
                    <div className="launch-us-links">
                      <a
                        id="launch-us-link-anchor"
                        href={d.usDropshipUrl || d.cjExactUrl || 'https://cjdropshipping.com'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-outline btn-sm"
                      >
                        🇺🇸 Open US Dropship Supplier Link ↗
                      </a>
                      <a
                        id="launch-us-backup-anchor"
                        href={d.usDropshipUrl || d.cjExactUrl || 'https://cjdropshipping.com'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-outline btn-sm"
                      >
                        📦 View US Stock Listing ↗
                      </a>
                      <button
                        type="button"
                        id="launch-auto-dropship-btn"
                        className="btn-gold btn-sm"
                        onClick={(e) => onTriggerAutoDropship(p, 'Launch Console Auto-Order Test', e.currentTarget)}
                      >
                        ⚡ Test Auto-Order (1 Unit)
                      </button>
                    </div>
                  </div>
                  <div className="launch-us-url-row">
                    <label htmlFor="launch-us-url-input" className="spec-micro-label">
                      DIRECT US DROPSHIP PRODUCT LINK (EDITABLE):
                    </label>
                    <div className="launch-us-url-input-group">
                      <input
                        type="url"
                        id="launch-us-url-input"
                        className="tabular"
                        placeholder="https://cjdropshipping.com/..."
                        value={usUrlInput}
                        onChange={(e) => setUsUrlInput(e.target.value)}
                      />
                      <button
                        type="button"
                        id="save-us-url-btn"
                        className="btn-outline btn-sm"
                        onClick={() => {
                          if (usUrlInput.trim()) {
                            onSaveCustomUsUrl(p.id, usUrlInput.trim());
                          }
                        }}
                      >
                        Save Link
                      </button>
                    </div>
                  </div>
                </div>

                <div className="calc-grid-clean">
                  <div className="field">
                    <label htmlFor="calc-fob" id="calc-fob-label">
                      {isUsDropship ? 'US Dropship Unit Cost ($)' : 'Alibaba Unit Cost ($)'}
                    </label>
                    <input
                      type="number"
                      id="calc-fob"
                      step="0.10"
                      min="0.50"
                      className="tabular"
                      value={fobUnit}
                      onChange={(e) => setFobUnit(e.target.value)}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="calc-ddp">Inbound Shipping ($/unit)</label>
                    <input
                      type="number"
                      id="calc-ddp"
                      step="0.10"
                      min="0.00"
                      className="tabular"
                      value={ddpUnit}
                      onChange={(e) => setDdpUnit(e.target.value)}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="calc-retail">
                      Sale Price ($){' '}
                      <span className="tabular" style={{ fontWeight: 600, color: '#71717A', fontSize: '0.78rem' }}>
                        (List:{' '}
                        <span style={{ textDecoration: 'line-through' }}>
                          {formatMoney(+(Math.ceil((parseFloat(retailPrice) || p.pricing.amazonRetailPrice) * 1.28) - 0.01).toFixed(2))}
                        </span>{' '}
                        <span style={{ color: '#DC2626', fontWeight: 800 }}>-21%</span>)
                      </span>
                    </label>
                    <input
                      type="number"
                      id="calc-retail"
                      step="0.50"
                      min="9.99"
                      className="tabular"
                      value={retailPrice}
                      onChange={(e) => setRetailPrice(e.target.value)}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="calc-batch">Order Quantity (Units)</label>
                    <input
                      type="number"
                      id="calc-batch"
                      step="1"
                      min="1"
                      className="tabular"
                      value={batchSize}
                      onChange={(e) => setBatchSize(e.target.value)}
                    />
                  </div>
                </div>

                <div className="channel-table-wrap">
                  <div className="table-caption">Compare Net Profit Across Your Stores (Click to Toggle Store Sync)</div>
                  <table className="clean-table">
                    <thead>
                      <tr>
                        <th scope="col">Storefront Channel</th>
                        <th scope="col" className="tabular">
                          Channel Fees
                        </th>
                        <th scope="col" className="tabular">
                          Net Profit / Unit
                        </th>
                        <th scope="col" className="tabular">
                          Net Margin
                        </th>
                        <th scope="col">Synced</th>
                      </tr>
                    </thead>
                    <tbody id="channel-comparison-tbody">
                      {stores.map((s) => {
                        const refFee = +(econ.amazonRetailPrice * s.referralRate).toFixed(2);
                        const fulFee = +(s.avgFulfillmentFee || 4.75).toFixed(2);
                        const totalFee = +(refFee + fulFee).toFixed(2);
                        const net = +(econ.amazonRetailPrice - econ.landedUnitCost - totalFee - econ.ppcEstUnit).toFixed(2);
                        const margin = +((net / (econ.amazonRetailPrice || 1)) * 100).toFixed(1);
                        const isSynced = Array.isArray(p.assignedStores) ? p.assignedStores.includes(s.id) : true;

                        return (
                          <tr key={s.id}>
                            <td>
                              <strong>{s.name}</strong>
                              <div className="table-store-sub">{s.channel}</div>
                            </td>
                            <td className="tabular">
                              {formatMoney(totalFee)} ({s.referralRateLabel})
                            </td>
                            <td className="tabular table-net-cell">+{formatMoney(net)}</td>
                            <td className="tabular table-margin-cell">{margin}%</td>
                            <td>
                              <button
                                type="button"
                                className={`sync-toggle-btn ${isSynced ? 'synced' : ''}`}
                                data-toggle-store={s.id}
                                aria-pressed={isSynced ? 'true' : 'false'}
                                aria-label={`Sync ${p.title} with ${s.name}`}
                                onClick={() => onToggleStoreSync(p.id, s.id)}
                              >
                                <span aria-hidden="true">{isSynced ? '✓' : '+'}</span> {isSynced ? 'Synced' : 'Sync'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <form id="po-dispatch-form" className="panel-card" noValidate onSubmit={handleQueuePo}>
                <div className="panel-header">
                  <span className="step-pill tabular">STEP 02</span>
                  <h3>Order from Alibaba Supplier &amp; Export Store Listing</h3>
                </div>

                <div className="calc-grid-clean two-col">
                  <div className="field">
                    <label htmlFor="brand-name">
                      Your Brand Name <span aria-hidden="true">*</span>
                    </label>
                    <input
                      ref={brandInputRef}
                      type="text"
                      id="brand-name"
                      required
                      aria-invalid={brandError ? 'true' : undefined}
                      aria-describedby="brand-name-error"
                      value={brandName}
                      placeholder="e.g., AURELIUS STUDIO"
                      onChange={(e) => {
                        setBrandName(e.target.value);
                        if (brandError) setBrandError(false);
                      }}
                    />
                    <p id="brand-name-error" className="field-error" role="alert" hidden={!brandError}>
                      Enter your brand name for FNSKU barcode and master carton labels.
                    </p>
                  </div>
                  <div className="field">
                    <label htmlFor="fba-warehouse">Destination Warehouse</label>
                    <select id="fba-warehouse" value={warehouse} onChange={(e) => setWarehouse(e.target.value)}>
                      <option value="Amazon FBA (ONT8 — California)">Amazon FBA (ONT8 — California)</option>
                      <option value="Shopify 3PL / Amazon MCF Hub">Shopify 3PL / Amazon MCF Hub</option>
                      <option value="Walmart Fulfillment Services (LAX1)">Walmart WFS (LAX1)</option>
                      <option value="TikTok Shop Fulfillment (FBT)">TikTok Shop (FBT)</option>
                    </select>
                  </div>
                </div>

                <div className="field">
                  <label htmlFor="rfq-script">Message to Alibaba Supplier (Includes Barcode &amp; Shipping Instructions)</label>
                  <textarea id="rfq-script" rows={4} value={rfqScript} onChange={(e) => setRfqScript(e.target.value)}></textarea>
                </div>

                <div className="action-row-clean">
                  <button
                    type="button"
                    className="btn-jet"
                    id="push-single-amazon-btn"
                    disabled={pushingAmazon}
                    onClick={async (e) => {
                      const trigger = e.currentTarget;
                      setPushingAmazon(true);
                      try {
                        await onPushSingleProductAmazon(
                          p,
                          {
                            retailPrice: parseFloat(retailPrice) || p.pricing.amazonRetailPrice,
                            brandName: brandName.trim() || 'AURELIUS'
                          },
                          trigger
                        );
                      } finally {
                        setPushingAmazon(false);
                      }
                    }}
                  >
                    {pushingAmazon ? 'Pushing Listing to Amazon SP-API...' : '⚡ 1-Click Sync to Amazon Seller Central'}
                  </button>
                  <button type="submit" className="btn-outline">
                    Queue Warehouse Purchase Order
                  </button>
                  <button
                    type="button"
                    className="btn-outline"
                    id="copy-rfq-btn"
                    onClick={() => {
                      navigator.clipboard.writeText(rfqScript);
                      showToast('Copied supplier message to clipboard!');
                      const url = p.alibabaSupplier?.alibabaUrl;
                      if (url) window.open(url, '_blank', 'noopener');
                    }}
                  >
                    Copy Message &amp; Open Alibaba <span aria-hidden="true">↗</span>
                  </button>
                  <a href={csvBlobUrl} download={`store-listing-${p.id}.csv`} id="export-csv-link" className="btn-outline">
                    Download Store Listing (.CSV)
                  </a>
                </div>

                {meta.status !== 'NOT_LISTED' && (
                  <div id="amazon-sync-receipt" className="sync-receipt-box">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <div>
                        <strong>
                          {meta.isActive ? '✓ ' : ''}
                          {meta.label} ({marketplaceId})
                        </strong>
                        <div className="tabular">
                          SKU: <code>{sku}</code> · ASIN: <code>{asin || '—'}</code>
                        </div>
                        <div style={{ fontSize: '0.78rem', color: '#A1A1AA', marginTop: 2 }}>
                          Store: <strong>BackdropsForLess</strong> (Seller ID: <code>{sellerId}</code>)
                        </div>
                        {meta.note && <div style={{ fontSize: '0.8rem', marginTop: 3 }}>{meta.note}</div>}
                      </div>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <a
                          href={amzUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-jet btn-sm"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', whiteSpace: 'nowrap' }}
                        >
                          <span>🛒 View on Amazon</span>
                          <span aria-hidden="true">↗</span>
                        </a>
                        <a
                          href={scUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-outline btn-sm"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', whiteSpace: 'nowrap' }}
                          title="Open SKU directly in Amazon Seller Central"
                        >
                          <span>⚙ Seller Central</span>
                          <span aria-hidden="true">↗</span>
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>

            <aside className="sticky-dossier" aria-label="Unit Economics Profit Summary">
              <div className="dossier-card">
                <div className="dossier-header">
                  <span className="dossier-tag">PROFIT SUMMARY</span>
                  <span className="dossier-rank tabular" id="dossier-store-label">
                    {activeStore ? activeStore.name : 'All Stores Avg'}
                  </span>
                </div>

                <div className="dossier-product">
                  <img id="dossier-img" src={p.images[0]} alt={p.title} />
                  <div className="dossier-product-meta">
                    <h3 id="dossier-title" className="truncate">
                      {p.title}
                    </h3>
                    <p id="dossier-supplier" className="truncate">
                      {isUsDropship ? d.usDropshipSupplier || 'US Domestic Dropship Hub (0 MOQ)' : p.alibabaSupplier?.name || 'Verified Alibaba Supplier'}
                    </p>
                  </div>
                </div>

                <div className="dossier-ledger">
                  <div className="ledger-row">
                    <span>Sale Price (Active Offer)</span>
                    <strong className="tabular" id="ledger-retail">
                      {formatMoney(econ.amazonRetailPrice)}
                    </strong>
                  </div>
                  <div className="ledger-row">
                    <span>Regular / List Price</span>
                    <span className="tabular" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ textDecoration: 'line-through', color: '#71717A' }}>
                        {formatMoney(
                          p.pricing.listPrice && p.pricing.listPrice > econ.amazonRetailPrice
                            ? p.pricing.listPrice
                            : +(Math.ceil(econ.amazonRetailPrice * 1.28) - 0.01).toFixed(2)
                        )}
                      </span>
                      <span
                        style={{
                          background: '#DC2626',
                          color: '#FFFFFF',
                          fontSize: '0.7rem',
                          fontWeight: 800,
                          padding: '1px 6px',
                          borderRadius: 4
                        }}
                      >
                        -{p.pricing.discountPct || 21}% SALE
                      </span>
                    </span>
                  </div>
                  <div className="ledger-row">
                    <span>Alibaba Cost + Shipping</span>
                    <span className="tabular" id="ledger-landed">
                      -{formatMoney(econ.landedUnitCost)}
                    </span>
                  </div>
                  <div className="ledger-row">
                    <span>Store Fees &amp; Est. Ads</span>
                    <span className="tabular" id="ledger-amz-fees">
                      -{formatMoney(econ.totalAmazonFees + econ.ppcEstUnit)}
                    </span>
                  </div>
                  <div className="ledger-divider"></div>
                  <div className="ledger-row highlight">
                    <span>Your Net Profit / Unit</span>
                    <strong className="tabular gold-text" id="ledger-net-unit">
                      +{formatMoney(econ.netProfitPerUnit)}
                    </strong>
                  </div>
                  <div className="ledger-row">
                    <span>Profit Margin &amp; ROI</span>
                    <span className="tabular" id="ledger-margin-roi">
                      {econ.netMarginPct}% Margin · {econ.roiPct}% ROI
                    </span>
                  </div>
                </div>

                <div className="dossier-batch-box">
                  <div className="batch-row">
                    <span>
                      Batch Cost (
                      <span id="ledger-batch-qty" className="tabular">
                        {econ.batchSize}
                      </span>{' '}
                      units)
                    </span>
                    <strong className="tabular" id="ledger-batch-cost">
                      {formatMoney(econ.totalBatchInvestment)}
                    </strong>
                  </div>
                  <div className="batch-row net">
                    <span>Expected Batch Profit</span>
                    <strong className="tabular gold-text" id="ledger-batch-profit">
                      +{formatMoney(econ.expectedBatchNetProfit)}
                    </strong>
                  </div>
                  <div className="batch-row">
                    <span>Est. Monthly Profit</span>
                    <strong className="tabular" id="ledger-monthly-profit">
                      ${monthlyProfit.toLocaleString()} / mo
                    </strong>
                  </div>
                </div>

                <div id="shipment-log-container" className="shipment-log" aria-live="polite">
                  {!shipments.length ? (
                    <div className="po-entry">
                      <div className="po-entry-sub">No purchase orders queued yet.</div>
                    </div>
                  ) : (
                    <>
                      <div className="shipment-heading tabular">RECENT WAREHOUSE ORDERS ({shipments.length})</div>
                      {shipments.slice(0, 2).map((s, idx) => (
                        <div key={s.poNumber + idx} className="po-entry">
                          <div className="po-entry-top">
                            <span className="tabular">{s.poNumber}</span>
                            <span className="tabular">{s.batchSize} units</span>
                          </div>
                          <div className="po-entry-sub truncate">{s.warehouse}</div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};
